import React, { useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Ticket, Wallet, X, Zap } from "lucide-react";
import { formatCash } from "../../utils/operatorConsoleHelpers";
import {
  combinedOdds,
  fmtOdd,
  maxStakeFor,
  minStakeFor,
  potentialWin,
} from "../../utils/bettingDeskHelpers";

const STAKE_FORM_ID = "pbd-stake-form";

const quickStakes = (min, max, cash) => {
  const base = min > 0 ? min : 100;
  const raw = [base, base * 2, base * 5, base * 10].filter((n) => n > 0);
  const capped = max > 0 ? raw.map((n) => Math.min(n, max)) : raw;
  const affordable = cash > 0 ? capped.filter((n) => n <= cash) : capped;
  return [...new Set(affordable)].slice(0, 4);
};

const PlaceBetStakeModal = ({
  open,
  house,
  slip,
  userCash,
  busy,
  error,
  onClose,
  onConfirm,
}) => {
  const errorId = useId();
  const minStake = minStakeFor(house);
  const maxStake = maxStakeFor(house);
  const totalOdds = combinedOdds(slip);
  const legCount = slip?.fixtures?.length || 0;
  const chips = useMemo(
    () => quickStakes(minStake, maxStake, userCash),
    [minStake, maxStake, userCash]
  );

  const [amount, setAmount] = useState("");
  const numeric = Number(String(amount).replace(/,/g, ""));
  const win = potentialWin(numeric, slip);

  useEffect(() => {
    if (open) {
      setAmount(minStake > 0 ? String(minStake) : chips[0] ? String(chips[0]) : "");
    }
  }, [open, minStake, chips]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const tooLow = minStake > 0 && numeric > 0 && numeric < minStake;
  const tooHigh = maxStake > 0 && numeric > maxStake;
  const insufficient = numeric > userCash;
  const invalid = !numeric || numeric <= 0 || tooLow || tooHigh || insufficient;

  const validationMessage =
    error ||
    (tooLow ? `Minimum stake is ${formatCash(minStake)}.` : "") ||
    (tooHigh ? `Maximum stake is ${formatCash(maxStake)}.` : "") ||
    (insufficient && numeric > 0 ? "Insufficient Cash in wallet." : "");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (invalid || busy) return;
    onConfirm?.(numeric);
  };

  return createPortal(
    <div className="pbd-stake-root" role="presentation" onClick={onClose}>
      <div
        className="pbd-stake-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Enter stake"
        aria-describedby={validationMessage ? errorId : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="pbd-stake-head">
          <div className="pbd-stake-head-copy">
            <p className="pbd-stake-kicker">Final step</p>
            <h2>Lock in your stake</h2>
          </div>
          <button type="button" className="pbd-stake-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <div className="pbd-stake-body">
          <div className="pbd-stake-summary" aria-label="Slip summary">
            <div className="pbd-stake-stat">
              <span className="pbd-stake-stat-label">Combined odds</span>
              <strong className="pbd-stake-stat-value tabular-nums">{fmtOdd(totalOdds)}</strong>
            </div>
            <div className="pbd-stake-stat">
              <span className="pbd-stake-stat-label">Legs on slip</span>
              <strong className="pbd-stake-stat-value tabular-nums">{legCount}</strong>
            </div>
            <div className="pbd-stake-stat">
              <span className="pbd-stake-stat-label">
                <Wallet size={10} aria-hidden />
                Wallet
              </span>
              <strong className="pbd-stake-stat-value tabular-nums">
                {formatCash(userCash, { compact: true })}
              </strong>
            </div>
          </div>

          <form id={STAKE_FORM_ID} className="pbd-stake-form" onSubmit={handleSubmit}>
            <div className="pbd-stake-field">
              <label className="pbd-stake-label" htmlFor="pbd-stake-input">
                Stake (Cash)
              </label>
              <input
                id="pbd-stake-input"
                type="number"
                min={minStake || 1}
                step="any"
                inputMode="decimal"
                className="pbd-stake-input tabular-nums"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
                aria-invalid={Boolean(validationMessage)}
              />
            </div>

            {chips.length > 0 ? (
              <div className="pbd-stake-chips" role="group" aria-label="Quick stake amounts">
                {chips.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    className={`pbd-stake-chip tabular-nums ${
                      numeric === chip ? "pbd-stake-chip-active" : ""
                    }`}
                    onClick={() => setAmount(String(chip))}
                  >
                    {formatCash(chip, { compact: true })}
                  </button>
                ))}
              </div>
            ) : null}

            <div className="pbd-stake-potential">
              <div className="pbd-stake-potential-copy">
                <Zap size={14} aria-hidden />
                <span>Potential return</span>
              </div>
              <strong key={`${numeric}-${win.toFixed(0)}`} className="pbd-stake-potential-value tabular-nums">
                {numeric > 0 ? formatCash(win, { compact: false }) : "—"}
              </strong>
            </div>

            {validationMessage ? (
              <p id={errorId} className="pbd-stake-error" role="alert">
                {validationMessage}
              </p>
            ) : null}
          </form>
        </div>

        <footer className="pbd-stake-foot">
          <button type="button" className="pbd-stake-cancel" onClick={onClose} disabled={busy}>
            Back
          </button>
          <button
            type="submit"
            form={STAKE_FORM_ID}
            className="pbd-stake-confirm"
            disabled={invalid || busy}
          >
            {busy ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <Ticket size={14} aria-hidden />}
            Place ticket
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default PlaceBetStakeModal;
